import React from 'react';
import {AdminSubscriptionPlan, AdminUserStatusFilter} from '../../domain/admin';
import {SearchIcon, XIcon} from '../Icons';

export interface AdminUserFilterValues {
  search: string;
  status: AdminUserStatusFilter | '';
  plan: AdminSubscriptionPlan | '';
  passwordSetFalse: boolean;
  inFlight: boolean;
  /** ISO timestamp. Set by the dashboard's "new in 30 days" tile, not by a control here. */
  createdAfter: string;
}

interface AdminUserFiltersProps {
  values: AdminUserFilterValues;
  /** The uncommitted search box text — the container debounces it into `values.search`. */
  searchInput: string;
  onSearchInput: (value: string) => void;
  onChange: (patch: Partial<AdminUserFilterValues>) => void;
  onClear: () => void;
  resultCount: number;
  isLoading: boolean;
}

const selectClass =
  'rounded-lg border border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-200 '
  + 'focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

/**
 * The filters worth a permanent control: search, subscription state, plan, "never set a password",
 * and "has a job running".
 *
 * `created_after` is supported by the endpoint but has no control here — it arrives from the
 * dashboard's "new in 30 days" tile, and a date picker for a value nobody types would be more
 * chrome than it earns. It still shows as a removable chip so an operator can see why the list is
 * short. `is_admin` is likewise omitted: a DB constraint pins admin to one account, so filtering
 * for it would always return that one row.
 *
 * Both filters read the **derived** subscription columns server-side, matching the badge in the
 * row beside them — so a lapsed Pro filters as free and badges as free.
 */
const AdminUserFilters: React.FC<AdminUserFiltersProps> = ({
  values,
  searchInput,
  onSearchInput,
  onChange,
  onClear,
  resultCount,
  isLoading,
}) => {
  const hasFilters = Boolean(
    values.search
    || values.status
    || values.plan
    || values.passwordSetFalse
    || values.inFlight
    || values.createdAfter
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-600" />
        <input
          type="search"
          value={searchInput}
          onChange={event => onSearchInput(event.target.value)}
          placeholder="Search email or name…"
          aria-label="Search users by email or name"
          className="w-60 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
           text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
           focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => onSearchInput('')}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/*
        `status` is the server's own vocabulary (active | inactive) and the labels say what it is
        assumed to mean — the derived subscription state, not "has ever signed in".
      */}
      <select
        value={values.status}
        onChange={event => onChange({status: event.target.value as AdminUserStatusFilter | ''})}
        aria-label="Subscription status"
        className={selectClass}
      >
        <option value="">Any subscription state</option>
        <option value="active">Subscription active</option>
        <option value="inactive">Subscription inactive</option>
      </select>

      <select
        value={values.plan}
        onChange={event => onChange({plan: event.target.value as AdminSubscriptionPlan | ''})}
        aria-label="Plan"
        className={selectClass}
      >
        <option value="">Any plan</option>
        <option value="free">Free</option>
        <option value="pro">Pro</option>
        <option value="team">Team</option>
      </select>

      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border
       border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-300">
        <input
          type="checkbox"
          checked={values.passwordSetFalse}
          onChange={event => onChange({passwordSetFalse: event.target.checked})}
          className="h-3 w-3 accent-indigo-500"
        />
        No password set
      </label>

      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border
       border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-300">
        <input
          type="checkbox"
          checked={values.inFlight}
          onChange={event => onChange({inFlight: event.target.checked})}
          className="h-3 w-3 accent-indigo-500"
        />
        Job running
      </label>

      {/*
        No control sets this, so without a chip the list would just be mysteriously short after
        arriving from the dashboard tile.
      */}
      {values.createdAfter && (
        <button
          type="button"
          onClick={() => onChange({createdAfter: ''})}
          title="Remove the joined-after filter"
          className="inline-flex items-center gap-1 rounded-lg border border-indigo-500/40
           bg-indigo-500/10 px-2.5 py-1.5 text-xs text-indigo-200 hover:bg-indigo-500/20 cursor-pointer"
        >
          Joined after {new Date(values.createdAfter).toLocaleDateString()}
          <XIcon className="h-3 w-3" />
        </button>
      )}

      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
        >
          Clear
        </button>
      )}

      <span className="ml-auto text-[11px] tabular-nums text-gray-600">
        {isLoading ? 'Loading…' : `${resultCount.toLocaleString()} matching`}
      </span>
    </div>
  );
};

export default AdminUserFilters;
